import {
	FileUnknownFilled,
	PaperClipOutlined,
	PlusSquareOutlined,
	StopOutlined,
	VideoCameraAddOutlined,
} from "@ant-design/icons"
import { Alert } from "components/Alert/Alert"
import { Button, type ButtonProps } from "components/Button/Button"
import { Input } from "components/Input/Input"
import { Radio, type RadioChangeEvent } from "components/Radio/Radio"
import { Tag, type TagProps } from "components/Tag/Tag"
import {
	Upload,
	type UploadChangeParam,
	type UploadFile,
} from "components/Upload/Upload"

import { ErrorCodeEnum } from "api/api"
import { Avatar } from "components/Avatar/Avatar"
import { BackButton } from "components/BackButton/BackButton"
import { Empty } from "components/Empty/Empty"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Image } from "components/Image/Image"
import { ImageThumbnail } from "components/Image/ImageThumbnail"
import { Modal } from "components/Modal/Modal"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Video } from "components/Video/Video"
import { VideoThumbnail } from "components/Video/VideoThumbnail"
import { useDisclosure } from "hooks/useDisclosure"
import { useElement } from "hooks/useElement"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { useRegionCapture } from "hooks/useRegionCapture"
import { useRightSidebar } from "hooks/useRightSidebar"
import { NoDataIcon } from "icons/NoDataIcon"
import { CreateTicketPayload, Ticket, TicketMessage } from "interface/support"
import { capitalize, lowerCase } from "lodash-es"
import { useFlag } from "modules/flags/flags"
import {
	ComponentProps,
	ElementType,
	Fragment,
	KeyboardEvent,
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from "react"
import { createPortal } from "react-dom"
import { useParams } from "react-router"
import { usePatientDetails } from "services/patients"
import {
	useAppendTicketMessage,
	useAttachment,
	useCloseTicket,
	useCreateTicket,
	useTicket,
	useTickets,
} from "services/support"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { asDate, dayjs, today } from "utils/date"
import { downloadFileFromUrl } from "utils/file"
import { groupMessages } from "./utils"

export function Support() {
	const { data: { isProvider } = {}, isFetched } = useUserRole()
	const { supportRequests } = useFlag("supportRequests")
	const targetScroll = useRef<HTMLUListElement>(null)
	const onBehalfOf = useOnBehalfOf({
		onUpdateOnBehalfOf: () => {
			editTicket.close()
			createTicket.close()
		},
	})
	const editTicket = useDisclosure<Ticket["_id"]>()
	const createTicket = useDisclosure({
		defaultIsOpen: onBehalfOf.initializeCreate,
	})
	const sidebarBackSlot = useElement("sidebar-back-slot", null)
	const {
		data: { pages: tickets = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = useTickets(onBehalfOf.patientId)
	const isLoading = isFetching || isFetchingNextPage
	const listAriaLabel = isLoading
		? "Waiting for support tickets"
		: tickets.length > 0
			? `${tickets.length} support ticket(s)`
			: "No support tickets yet"
	const handleBack = useCallback(() => {
		if (editTicket.isOpen) {
			editTicket.close()
		} else if (createTicket.isOpen) {
			createTicket.close()
		}
	}, [createTicket, editTicket])
	const handleCreate = useCallback(() => {
		createTicket.open()
		editTicket.close()
	}, [createTicket, editTicket])

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	if (!supportRequests.enabled || !isFetched) {
		return null
	}

	return (
		<div className="flex h-full min-h-0 min-w-0 flex-col">
			{(editTicket.isOpen || createTicket.isOpen) &&
				sidebarBackSlot &&
				createPortal(
					<BackButton
						onClick={handleBack}
						aria-label="Back to support tickets"
					/>,
					sidebarBackSlot,
				)}

			{!editTicket.isOpen && !createTicket.isOpen && (
				<Spinner spinning={isLoading}>
					<div className="flex h-full flex-col gap-5">
						{isProvider && onBehalfOf.label}

						{!isLoading && tickets.length === 0 && (
							<Empty
								Icon={NoDataIcon}
								description={
									<>
										No tickets yet.
										<br />
										Create your first ticket using the button below.
									</>
								}
								className="justify-start"
							/>
						)}

						<ul
							ref={targetScroll}
							className="flex h-full max-h-full flex-1 flex-col gap-4 overflow-y-auto"
							aria-label={listAriaLabel}
						>
							{tickets.map((ticket) => {
								return (
									<Card
										as="li"
										key={`ticket-${ticket._id}`}
										tabIndex={0}
										ticket={ticket}
										className={cn(
											"focus-outline hover:cursor-pointer",
											ticket.status === "Closed" && "bg-white",
										)}
										onClick={() => editTicket.open(ticket._id)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault()
												editTicket.open(ticket._id)
											}
										}}
									/>
								)
							})}
						</ul>
						<CreateRequestButton onClick={handleCreate} />
					</div>
				</Spinner>
			)}

			{editTicket.isOpen && editTicket.value && (
				<EditTicket ticketId={editTicket.value} onCreate={handleCreate} />
			)}

			{createTicket.isOpen && <CreateTicket onClose={createTicket.close} />}
		</div>
	)
}

function CreateTicket({ onClose }: { onClose: VoidFunction }) {
	const onBehalfOf = useOnBehalfOf({ onClose })
	const { data: { fullName, isPatient } = {}, isFetched } = useUserRole()
	const [isNonMedicalConfirmed, setIsNonMedicalConfirmed] = useState(false)
	const [newTicket, setNewTicket] = useState<Ticket>()
	const payload = useMemo<Partial<CreateTicketPayload>>(
		() => ({
			isNonMedicalConfirmed,
			patientContextId: onBehalfOf.patientId,
		}),
		[isNonMedicalConfirmed, onBehalfOf.patientId],
	)
	const welcomeMsg = `Hi ${fullName}, we're happy to help you with your request!`
	const issueDetailsMsg =
		"Please provide details about your issue: what were you trying to do and what didn't work?\nDo you happen to remember roughly when this issue occurred?"
	const composerDisabled =
		(isPatient && !isNonMedicalConfirmed) ||
		(onBehalfOf.enabled && !onBehalfOf.confirmed)
	const handleValidateMessage = useCallback<
		NonNullable<ComponentProps<typeof MessageComposer>["onValidateMessage"]>
	>(
		({ text, files = [] }) => {
			const hasText = !!text && text.length > 0
			return !!newTicket ? hasText || files.length > 0 : hasText
		},
		[newTicket],
	)

	if (!isFetched) {
		return null
	}

	return (
		<>
			{onBehalfOf.warning}

			<div className="flex min-h-0 flex-1 flex-col justify-end gap-5">
				<div className="flex flex-col gap-4 overflow-y-auto p-1">
					{onBehalfOf.confirmation}

					{!isPatient && !onBehalfOf.enabled && (
						<>
							<MessageItem as="article">{welcomeMsg}</MessageItem>
							<MessageItem as="article">{issueDetailsMsg}</MessageItem>
						</>
					)}

					{isPatient && (
						<>
							<MessageItem as="article" className="mt-auto">
								{welcomeMsg}
							</MessageItem>
							<MessageItem as="article">
								Please confirm that your enquiry is for non-medical support
								only.
							</MessageItem>
							{!isNonMedicalConfirmed && (
								<Button
									className="w-fit self-end"
									onClick={() => setIsNonMedicalConfirmed(true)}
								>
									I confirm that this is a non-medical request
								</Button>
							)}
							{isNonMedicalConfirmed && (
								<>
									<MessageItem as="article" isAlignedRight>
										I confirm that this is a non-medical request
									</MessageItem>
									<MessageItem as="article">{issueDetailsMsg}</MessageItem>
								</>
							)}
						</>
					)}

					{newTicket && (
						<MessageList ticketId={newTicket._id} className="overflow-visible">
							<MessageItem>
								<div>
									Thank you! A ticket was created for the Empo support team. You
									can always review the status of your ticket{" "}
									{newTicket.identifier} under{" "}
									<Button
										type="link"
										className="h-auto p-0 font-bold text-green-80"
										onClick={onClose}
									>
										Support tickets
									</Button>
									.
								</div>
							</MessageItem>
							<MessageItem>
								Is there anything you would like to add to the request? You can
								add images, videos or screen recordings using the paperclip
								button below.
							</MessageItem>
						</MessageList>
					)}
				</div>

				<MessageComposer
					ticketId={newTicket?._id}
					disabled={composerDisabled}
					payload={payload}
					onCreateTicket={setNewTicket}
					onValidateMessage={handleValidateMessage}
				/>
			</div>
		</>
	)
}

function EditTicket({
	ticketId,
	onCreate,
}: {
	ticketId: Ticket["_id"]
	onCreate: VoidFunction
}) {
	const onBehalfOf = useOnBehalfOf()
	const { data: ticket, isFetched, isFetching } = useTicket(ticketId)
	const isClosedAfter30days =
		ticket &&
		["Closed", "Cancelled"].includes(ticket.status) &&
		today().diff(ticket.completedAt, "day") >= 30

	if (isFetched && !ticket) {
		return null
	}

	return (
		<Spinner spinning={isFetching} classNames={{ root: "min-h-0 flex-1" }}>
			<div className="flex h-full flex-col gap-5">
				{onBehalfOf.label}
				{ticket && <Card as="section" ticket={ticket} showClose />}
				<MessageList ticket={ticket} className="grow" />
				{onBehalfOf.warning}
				{!isClosedAfter30days && <MessageComposer ticketId={ticketId} />}
				{isClosedAfter30days && (
					<div>
						<CreateRequestButton onClick={onCreate} />
					</div>
				)}
			</div>
		</Spinner>
	)
}

function CreateRequestButton({ className, ...props }: ButtonProps) {
	return (
		<Button
			{...props}
			size="large"
			icon={<PlusSquareOutlined aria-hidden />}
			className={cn("w-full", className)}
		>
			Create new request
		</Button>
	)
}

function MessageComposer({
	ticketId,
	disabled = false,
	payload,
	onCreateTicket,
	onValidateMessage,
}: {
	ticketId?: string
	disabled?: boolean
	payload?: Partial<CreateTicketPayload>
	onCreateTicket?: (newTicket: Ticket) => void
	onValidateMessage?: (state: Partial<MessagePayload>) => boolean
}) {
	const [{ text, files = [] }, dispatch] = useReducer(
		(state: MessagePayload, action: Partial<MessagePayload>) => {
			return { ...state, ...action }
		},
		{ text: "", files: [] },
	)
	const isMessageValid = useMemo(() => {
		if (onValidateMessage) {
			return onValidateMessage({ text, files })
		}
		return (!!text && text.length > 0) || files.length > 0
	}, [files, onValidateMessage, text])
	const errorStatus = useDisclosure<string>()
	const createTicket = useCreateTicket()
	const appendTicketMessage = useAppendTicketMessage(ticketId)
	const isLoading = createTicket.isPending || appendTicketMessage.isPending
	const handleFileChange = useCallback(
		(param: UploadChangeParam) => {
			const fileToUpload = param.file as unknown as File
			if (!FILE_CONSTRAINTS.ALLOWED_MIME_TYPES.includes(fileToUpload.type)) {
				errorStatus.open(
					`The supported file types are ${FILE_CONSTRAINTS.ALLOWED_MIME_TYPES_WKN.join(", ")}`,
				)
			} else if (fileToUpload.size > FILE_CONSTRAINTS.MAX_FILE_SIZE) {
				errorStatus.open(FILE_CONSTRAINTS.MAX_SIZE_ERROR_MESSAGE)
			} else if (files.length > FILE_CONSTRAINTS.MAX_FILE_COUNT) {
				errorStatus.open(String(FILE_CONSTRAINTS.MAX_COUNT_ERROR_MESSAGE))
			} else {
				errorStatus.close()
				dispatch({ files: [...files, fileToUpload] })
			}
		},
		[errorStatus, files],
	)
	const handleSend = useCallback(() => {
		const baseErrorMessage =
			"An unexpected error occurred while sending the message"
		const genericErrorMessage = `${baseErrorMessage}. Please, try again.`

		if (ticketId) {
			appendTicketMessage.mutate(
				{ content: text, files },
				{
					onSuccess() {
						dispatch({ text: "", files: [] })
					},
					onError(error) {
						const isErrorCodeKnown = [
							ErrorCodeEnum.LinearIssueNotFoundError,
							ErrorCodeEnum.SupportTicketNotFoundError,
							ErrorCodeEnum.LinearAttachmentNotFoundError,
						].includes(error.code)

						if (isErrorCodeKnown) {
							errorStatus.open(`${baseErrorMessage}: ${error.message}`)
						} else {
							errorStatus.open(genericErrorMessage)
						}
					},
				},
			)
		} else {
			createTicket.mutate(
				{ ...payload, initialMessage: text, files },
				{
					onSuccess(newTicket) {
						onCreateTicket?.(newTicket)
						dispatch({ text: "", files: [] })
					},
					onError() {
						errorStatus.open(genericErrorMessage)
					},
				},
			)
		}
	}, [
		appendTicketMessage,
		createTicket,
		errorStatus,
		files,
		onCreateTicket,
		payload,
		text,
		ticketId,
	])
	const handlePressEnter = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter") {
				if (e.shiftKey || e.altKey) {
					e.preventDefault()
					dispatch({ text: text + "\n" })
				} else {
					e.preventDefault()
					handleSend()
				}
			}
		},
		[handleSend, text],
	)
	const handleOnTextChange = useCallback(
		(value = "") => {
			dispatch({ text: value.length > 0 ? value : undefined })
			if (value !== text) {
				errorStatus.close()
			}
		},
		[errorStatus, text],
	)
	const handleRecordComplete = useCallback(
		(fileRecord: File) => {
			const file = fileRecord as unknown as UploadFile
			handleFileChange({ file, fileList: [file] })
		},
		[handleFileChange],
	)

	return (
		<footer
			className={cn(
				"space-y-2.5 rounded-lg border border-grey-20 bg-white p-3 transition-all duration-300 hover:border-green-20 has-focus:border-black",
				disabled && "bg-grey-20",
			)}
		>
			<Input.TextArea
				key={files?.map(({ lastModified }) => lastModified).join("-")}
				size="large"
				disabled={disabled}
				readOnly={isLoading}
				maxLength={737}
				name="text"
				value={text}
				placeholder={"Add message\u2026"}
				autoSize={{ minRows: 1, maxRows: 10 }}
				className="rounded-none border-none p-0"
				onPressEnter={handlePressEnter}
				onChange={({ target: { value = "" } }) => handleOnTextChange(value)}
			/>
			<div className="flex w-full justify-between gap-1">
				<div className="flex items-end gap-1">
					{files.length < 3 && (
						<>
							<Upload
								data-testid="Select a file"
								maxCount={1}
								name="files"
								accept={FILE_CONSTRAINTS.ALLOWED_MIME_TYPES.join(",")}
								showUploadList={false}
								beforeUpload={() => false}
								disabled={disabled}
								onChange={handleFileChange}
							>
								<Tooltip.Dark title="Select a file">
									<Button
										size="large"
										aria-label="Select a file"
										className="select-none"
										disabled={disabled}
										icon={<PaperClipOutlined aria-hidden />}
									/>
								</Tooltip.Dark>
							</Upload>
							<RecordRegion
								disabled={disabled}
								onComplete={handleRecordComplete}
							/>
						</>
					)}

					{files?.map((file) => {
						const Thumbnail = file.type.includes("video")
							? VideoThumbnail
							: ImageThumbnail
						return (
							<Thumbnail
								key={file.name}
								disabled={disabled}
								file={file}
								onRemove={() => {
									dispatch({
										files: files.filter(({ name }) => name !== file.name),
									})
								}}
							/>
						)
					})}
				</div>

				<Button
					type="primary"
					size="large"
					disabled={disabled || !isMessageValid}
					loading={isLoading}
					onClick={handleSend}
				>
					Send
				</Button>
			</div>

			<ErrorStatus show={errorStatus.isOpen}>{errorStatus.value}</ErrorStatus>
		</footer>
	)
}

function RecordRegion({
	onComplete,
	...props
}: ButtonProps & {
	onComplete: (file: File) => void
}) {
	const { startCapture, stopCapture, isCapturing, isSupported } =
		useRegionCapture({
			onComplete,
		})

	if (!isSupported) {
		return null
	}

	return (
		<>
			{!isCapturing && (
				<Tooltip.Dark title="Start recording">
					<Button
						{...props}
						size="large"
						aria-label="Start recording"
						className="select-none"
						icon={<VideoCameraAddOutlined aria-hidden />}
						onClick={async () =>
							await startCapture(document.getElementById("layout-content")!)
						}
					/>
				</Tooltip.Dark>
			)}
			{isCapturing && (
				<Tooltip.Dark title="Stop recording">
					<Button
						{...props}
						danger
						size="large"
						aria-label="Stop recording"
						className="select-none"
						icon={<StopOutlined aria-hidden />}
						onClick={stopCapture}
					/>
				</Tooltip.Dark>
			)}
		</>
	)
}

function MessageList({
	ref,
	ticketId,
	className,
	children,
	ticket: sourceTicket,
	...props
}: ComponentProps<"div"> & { ticket?: Ticket; ticketId?: Ticket["_id"] }) {
	const targetScroll = useRef<HTMLDivElement>(null)
	const { data: ticket = sourceTicket } = useTicket(ticketId)
	const groupedMessages = useMemo(
		() => groupMessages(ticket?.messages ?? []),
		[ticket?.messages],
	)

	useLayoutEffect(() => {
		const element = ref
			? (ref as RefObject<HTMLDivElement>).current
			: targetScroll.current
		if (element) {
			element.scrollTop = element.scrollHeight
		}
	}, [groupedMessages, ref])

	return (
		<div
			{...props}
			ref={targetScroll}
			className={cn("flex flex-col gap-4 overflow-y-auto p-1", className)}
		>
			{groupedMessages.map(({ time, authorGroups }, groupIndex) => {
				const messageTimestamp = asDate(time).format("YYYY MMM D h:mm A")
				const ariaLabel = `Messages starting ${messageTimestamp}`

				return (
					<article
						key={time}
						aria-label={ariaLabel}
						className="flex flex-col gap-1"
					>
						<div className="text-center text-sm text-navy-60">
							{messageTimestamp}
						</div>
						{authorGroups.map(
							(
								{ authorDisplayName, authorType, messages },
								authorGroupIndex,
							) => {
								const isAlignedRight = authorType === "CREATOR"
								const isAutomated = authorType === "AUTO_REPLY"

								return (
									<div
										key={`${authorDisplayName}-${messages[0].id}`}
										className="flex flex-col gap-1"
									>
										{!isAutomated && (
											<p
												className={cn(
													"px-10 text-sm text-navy-60",
													isAlignedRight && "text-right",
												)}
											>
												{authorDisplayName}
											</p>
										)}

										<ul
											aria-label={authorDisplayName}
											className="flex flex-col gap-4"
										>
											{messages.map((message, messageIndex) => {
												const isFirstMessage = [
													groupIndex,
													authorGroupIndex,
													messageIndex,
												].every((i) => i === 0)
												const showAvatar =
													isFirstMessage || messageIndex === messages.length - 1
												const messageTimestamp = asDate(
													message.createdAt,
												).format("YYYY MMM D h:mm:ss A")
												const messageAriaLabel = `Message by ${authorDisplayName} at ${messageTimestamp}`

												return (
													<Fragment key={message.id}>
														<MessageItem
															tabIndex={0}
															isAutomated={isAutomated}
															isAlignedRight={isAlignedRight}
															aria-label={messageAriaLabel}
															showAvatar={showAvatar}
															message={message}
															className={cn(isAutomated && "mt-4")}
														/>
														{isFirstMessage && children}
													</Fragment>
												)
											})}
										</ul>
									</div>
								)
							},
						)}
					</article>
				)
			})}

			{ticket && <CloseTicket key={ticket._id} ticket={ticket} />}
		</div>
	)
}

const MessageItem = <C extends ElementType = "li">({
	as,
	isAutomated = false,
	isAlignedRight = false,
	showAvatar = false,
	message,
	className,
	children,
	...props
}: { as?: C } & Omit<ComponentProps<C>, "as"> & {
		isAutomated?: boolean
		isAlignedRight?: boolean
		showAvatar?: boolean
		message?: TicketMessage
	}) => {
	const Component = as || "li"
	const { id, authorDisplayName, content, attachments = [] } = message ?? {}
	const text = children ?? content

	return (
		<Component
			{...props}
			tabIndex={0}
			className={cn("focus-outline rounded-xl outline-offset-1!", className)}
		>
			<div
				className={cn(
					"flex w-full items-end justify-start gap-2",
					isAlignedRight && "justify-end",
				)}
			>
				{authorDisplayName && (
					<Avatar
						className={cn(
							"invisible order-1 size-8 lg:size-8",
							showAvatar && "visible",
							isAlignedRight && "order-2",
							isAutomated && "invisible",
						)}
					>
						{authorDisplayName}
					</Avatar>
				)}

				<div
					className={cn(
						"flex flex-col gap-1 px-3 py-2 wrap-break-word whitespace-pre-wrap",
						"order-2 max-w-[70%] rounded-xl rounded-bl-xs bg-grey-20 text-navy-100",
						isAlignedRight && "order-1 rounded-br-xs rounded-bl-xl bg-mint-100",
						isAutomated && "border border-grey-20 bg-white",
					)}
				>
					{text}

					{attachments.map((url, index) => (
						<MessageAttachment
							key={`message-${id}-attachment-${index}`}
							url={url}
							theme={isAlignedRight ? "light" : "dark"}
						/>
					))}
				</div>
			</div>
		</Component>
	)
}

function MessageAttachment({
	url,
	theme,
}: {
	url: string
	theme: "light" | "dark"
}) {
	const { data: attachmentBase64, isFetched } = useAttachment(url)
	const [mediaLoadSuccess, setMediaLoadSuccess] = useState(true)
	const isImage = attachmentBase64?.includes("data:image/")
	const isVideo = attachmentBase64?.includes("data:video/")
	const isUnknown = !isImage && !isVideo

	if (!isFetched || !attachmentBase64) {
		return null
	}

	return (
		<>
			{(isUnknown || !mediaLoadSuccess) && (
				<>
					<div className="flex gap-2">
						<FileUnknownFilled
							aria-hidden
							className={cn("text-green-60", theme === "light" && "text-white")}
						/>
						<Tooltip title={"Download file"}>
							<p className="text-wrap">File</p>
						</Tooltip>
					</div>

					<Button
						type={theme === "dark" ? "primary" : undefined}
						className="w-fit"
						onClick={async () => {
							await downloadFileFromUrl(attachmentBase64)
						}}
					>
						Download
					</Button>
				</>
			)}

			{isImage && mediaLoadSuccess && (
				<Image
					alt=""
					src={attachmentBase64}
					className="max-h-50 min-h-50 object-cover"
					preview
					loading="lazy"
					onError={() => {
						setMediaLoadSuccess(false)
					}}
				/>
			)}

			{isVideo && mediaLoadSuccess && (
				<Video
					src={attachmentBase64}
					controls
					className="max-h-50 min-h-50"
					onError={() => {
						setMediaLoadSuccess(false)
					}}
				/>
			)}
		</>
	)
}

function CloseTicket({ ticket }: { ticket: Ticket }) {
	const closeSuccess = useDisclosure()
	const moreDetails = useDisclosure()
	const closeTicket = useCloseTicket()
	const latestMessage = (ticket.messages ?? []).at(-1)
	const isClosable =
		ticket.status === "NeedsFeedback" &&
		latestMessage &&
		latestMessage.authorType === "SUPPORT_TEAM"
	const wasClosedSuccessfully = closeTicket.isSuccess || closeSuccess.isOpen
	const radioDisabled =
		ticket.status !== "NeedsFeedback" || wasClosedSuccessfully
	const handleChange = useCallback(
		(e: RadioChangeEvent) => {
			const { value } = e.target

			if (!value) {
				closeTicket.reset()
				moreDetails.open()
				return
			}

			moreDetails.close()

			closeTicket.mutate(ticket._id, {
				onSuccess() {
					closeSuccess.open()
				},
			})
		},
		[closeSuccess, closeTicket, moreDetails, ticket._id],
	)

	if (!isClosable && !wasClosedSuccessfully) {
		return null
	}

	return (
		<article aria-labelledby="close-ticket" className="flex flex-col gap-1">
			<MessageItem as="div" id="close-ticket">
				Was your issue resolved?
			</MessageItem>
			<div className="flex flex-col gap-1 self-end text-right">
				<Radio.Group
					optionType="button"
					disabled={radioDisabled}
					options={[
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					]}
					onChange={handleChange}
				/>
				<ErrorStatus show={closeTicket.isError}>
					{getCloseErrorMessage(ticket)}
				</ErrorStatus>
			</div>
			{moreDetails.isOpen && (
				<MessageItem as="div">
					Could you please share more details about the issue you're facing?
				</MessageItem>
			)}
			{closeSuccess.isOpen && (
				<MessageItem as="div">
					Great! We'll let the team know the issue is resolved and will close
					this ticket. You can re-open this ticket within 30 days by adding a
					message below.
				</MessageItem>
			)}
		</article>
	)
}

const Card = <C extends ElementType = "div">({
	as,
	ticket,
	showClose = false,
	...props
}: { as?: C } & Omit<ComponentProps<C>, "as"> & {
		ticket: Ticket
		showClose?: boolean
	}) => {
	const Component = as || "div"
	const { identifier, initialMessage, status, title } = ticket
	const isClosable = ["Open", "NeedsFeedback"].includes(status) && showClose
	const closeDisclose = useDisclosure()
	const closeTicket = useCloseTicket()
	const handleClose = useCallback(() => {
		closeTicket.mutate(ticket._id, { onSuccess: closeDisclose.close })
	}, [closeDisclose, closeTicket, ticket._id])

	return (
		<Component
			{...props}
			aria-label={ticket.identifier}
			className={cn(
				"flex flex-col gap-3 rounded-lg border border-mint-20 bg-mint-0 p-4",
				props.className,
			)}
		>
			<div className="flex justify-between">
				<div className="text-sm text-navy-60">{identifier}</div>
				<Tag variant="filled" color={getStatusColor(status)} className="m-0">
					{capitalize(lowerCase(status))}
				</Tag>
			</div>
			<div className="flex flex-col">
				<h3 className="text-lg font-bold text-navy-100">{title}</h3>
				<p className="line-clamp-2">{initialMessage}</p>
			</div>
			<div
				className={cn(
					"flex justify-end gap-1",
					isClosable && "justify-between",
				)}
			>
				{isClosable && (
					<Button size="small" onClick={closeDisclose.open}>
						Close ticket
					</Button>
				)}
				<p className="text-sm text-navy-60">{getLastActivityAt(ticket)}</p>
			</div>

			{closeDisclose.isOpen && (
				<Modal
					open={closeDisclose.isOpen}
					confirmLoading={closeTicket.isPending}
					title="Do you really want to close this ticket?"
					okText="Yes, close ticket"
					okButtonProps={{ danger: true }}
					onOk={handleClose}
					cancelText="Cancel"
					onCancel={closeDisclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					Our support team will no longer investigate your issue. You may
					re-open the ticket by adding a message within 30 days after closing.
					<ErrorStatus show={closeTicket.isError}>
						{getCloseErrorMessage(ticket)}
					</ErrorStatus>
				</Modal>
			)}
		</Component>
	)
}

function useOnBehalfOf({
	onClose,
	onUpdateOnBehalfOf,
}: {
	onClose?: VoidFunction
	onUpdateOnBehalfOf?: VoidFunction
} = {}) {
	const { userId: patientId } = useParams()
	const { sidebarState } = useRightSidebar()
	const onBehalfOf = !!sidebarState?.onBehalfOf
	const target = `${patientId}-${onBehalfOf}`
	const prevTarget = useRef(target)
	const { data: patient, isFetched } = usePatientDetails(patientId)
	const patientConfirmed = useDisclosure()
	const enabled = !!patientId && !!patient && isFetched && !!onBehalfOf
	const initializeCreate = enabled && !!sidebarState?.initializeCreate
	const warning = enabled ? (
		<Alert
			showIcon
			title={
				<>
					Information shared in this request will <u>not</u> be exposed to the
					patient.
				</>
			}
		/>
	) : null
	const confirmation = enabled ? (
		<>
			<MessageItem as="article">
				<p>
					You are about to create a ticket on behalf of{" "}
					<b>{patient?.fullName}</b>. Is that correct?
				</p>
			</MessageItem>
			<article className="flex gap-2 self-end" aria-label="Confirm patient">
				<Button type="primary" onClick={patientConfirmed.open}>
					Yes, correct!
				</Button>
				<Button onClick={onClose}>Cancel</Button>
			</article>
			{patientConfirmed.isOpen && (
				<MessageItem as="article">
					Please provide details about your patient's issue: what did they try
					to achieve, what didn't work and did they use a workaround?
					<br />
					Do you happen to remember roughly when this issue occurred?
				</MessageItem>
			)}
		</>
	) : null
	const label = enabled ? (
		<p>
			Showing tickets for{" "}
			<span className="font-semibold">{patient?.fullName}</span>
		</p>
	) : null

	useEffect(() => {
		if (onBehalfOf && prevTarget.current !== target) {
			onUpdateOnBehalfOf?.()
		}

		return () => {
			prevTarget.current = target
		}
	}, [onUpdateOnBehalfOf, onBehalfOf, target])

	if (!enabled) {
		return { enabled }
	}

	return {
		enabled,
		patient,
		patientId,
		warning,
		confirmation,
		confirmed: patientConfirmed.isOpen,
		label,
		initializeCreate,
	}
}

function getCloseErrorMessage(ticket: Ticket) {
	return `An unexpected error occurred while closing the ticket ${ticket.identifier}. Please, try again.`
}

function getLastActivityAt({
	status,
	createdAt,
	updatedAt,
	completedAt,
}: Ticket) {
	let lastActivityAt = ""
	if (status === "Open") {
		lastActivityAt = `Opened ${dayjs(createdAt).fromNow()}`
	} else if (status === "NeedsFeedback") {
		lastActivityAt = `Updated ${dayjs(updatedAt).fromNow()}`
	} else if (status === "Closed" || status === "Cancelled") {
		lastActivityAt = completedAt
			? `${status} ${dayjs(completedAt).fromNow()}`
			: "Closed"
	}
	return lastActivityAt
}

function getStatusColor(status: Ticket["status"]) {
	let color: TagProps["color"] = "default"
	if (status === "Open") {
		color = "geekblue"
	} else if (status === "NeedsFeedback") {
		color = "red"
	}
	return color
}

interface MessagePayload {
	text: string
	files?: File[]
}

const FILE_CONSTRAINTS = {
	MAX_FILE_COUNT: 3,
	MAX_FILE_SIZE: 10 * 1024 * 1024, // 49MB in bytes
	ALLOWED_MIME_TYPES: [
		"image/jpeg",
		"image/png",
		"image/gif",
		"image/webp",
		"video/mp4",
		"video/quicktime",
		"video/webm",
	],
	ALLOWED_MIME_TYPES_WKN: [
		"JPEG/JPG",
		"PNG",
		"GIF",
		"WebP",
		"MP4",
		"QUICKTIME",
		"WebM",
	],
	MAX_SIZE_ERROR_MESSAGE: "File size exceeds maximum limit of 10MB",
	MAX_COUNT_ERROR_MESSAGE: "Max of 3 files allowed to upload at the same time",
}
